import { useState } from "react";
import { format } from "date-fns";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { CalendarIcon, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const fld = "bg-secondary border-white/10 text-foreground rounded-none h-11";
const lbl = "text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block";

export default function EditReservationDialog({ booking, vehicles, onDone, testId }) {
  const routesLib = useMapsLibrary("routes");
  const q = booking.quote;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    service_type: q.service_type || "transfer",
    pickup: q.pickup || "", dropoff: q.dropoff || "",
    time: q.time || "10:00", passengers: String(q.passengers || 2),
    vehicle_type: q.vehicle_type || "sedan", meet_greet: !!q.meet_greet,
    hours: String(q.hours || 2),
    flight_number: booking.flight_number || "", notes: booking.notes || "",
  });
  const [pickupCoord, setPickupCoord] = useState(q.pickup_lat ? { lat: q.pickup_lat, lng: q.pickup_lng } : null);
  const [dropoffCoord, setDropoffCoord] = useState(q.dropoff_lat ? { lat: q.dropoff_lat, lng: q.dropoff_lng } : null);
  const [date, setDate] = useState(q.date ? new Date(`${q.date}T00:00:00`) : null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const maxPax = vehicles.find((v) => v.id === form.vehicle_type)?.pax || 6;

  const getDist = async () => {
    if (form.service_type === "hourly" || !routesLib || !pickupCoord || !dropoffCoord) return { miles: null, duration: null };
    try {
      const svc = new routesLib.DirectionsService();
      const res = await svc.route({ origin: pickupCoord, destination: dropoffCoord, travelMode: window.google.maps.TravelMode.DRIVING });
      const legs = res.routes[0].legs;
      const meters = legs.reduce((s, l) => s + l.distance.value, 0);
      const secs = legs.reduce((s, l) => s + l.duration.value, 0);
      return { miles: Math.round((meters / 1609.34) * 10) / 10, duration: `${Math.round(secs / 60)} min` };
    } catch { return { miles: null, duration: null }; }
  };

  const save = async () => {
    if (!date) { toast.error("Select a date."); return; }
    if (form.service_type === "transfer" && !dropoffCoord) { toast.error("Select a drop-off from the suggestions."); return; }
    setSaving(true);
    try {
      const dist = await getDist();
      const payload = {
        ...form, passengers: Number(form.passengers), hours: Number(form.hours),
        date: format(date, "yyyy-MM-dd"),
        pickup_lat: pickupCoord?.lat, pickup_lng: pickupCoord?.lng,
        dropoff_lat: dropoffCoord?.lat, dropoff_lng: dropoffCoord?.lng,
        distance_miles: dist.miles, duration_text: dist.duration,
        stops: q.stops || [], promo_code: q.promo_code || null,
        tip_amount: q.tip ?? 0,
        origin_url: window.location.origin,
      };
      const { data } = await api.post(`/bookings/${booking.id}/edit`, payload);
      if (data.status === "payment_required") {
        toast.info(`An extra $${data.amount.toFixed(2)} is due — sending you to secure payment...`);
        window.location.href = data.url;
        return;
      }
      if (data.refunded > 0) toast.success(`Reservation updated. $${data.refunded.toFixed(2)} refunded to your card.`);
      else toast.success("Reservation updated.");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid={testId} className="border-white/20 rounded-none hover:border-primary hover:text-primary">
          <Pencil size={14} className="mr-1.5" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground font-heading">Edit Reservation #{booking.confirmation_code}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Changing the trip may adjust the price. If it costs more, we will send you to pay the difference; if it is less, we refund your card automatically.</p>

        <div className="space-y-4 py-2">
          <div>
            <label className={lbl}>Service</label>
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
              <SelectTrigger data-testid="edit-service" className={fld}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-foreground">
                <SelectItem value="transfer">Point-to-Point</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className={lbl}>Pickup</label>
            <AddressAutocomplete value={form.pickup} placeholder="Pickup address" testId="edit-pickup"
              onChange={(v) => set("pickup", v)}
              onSelect={(p) => { set("pickup", p.address); setPickupCoord({ lat: p.lat, lng: p.lng }); }} />
          </div>

          {form.service_type === "transfer" ? (
            <div>
              <label className={lbl}>Drop-off</label>
              <AddressAutocomplete value={form.dropoff} placeholder="Drop-off address" testId="edit-dropoff"
                onChange={(v) => { set("dropoff", v); setDropoffCoord(null); }}
                onSelect={(p) => { set("dropoff", p.address); setDropoffCoord({ lat: p.lat, lng: p.lng }); }} />
            </div>
          ) : (
            <div>
              <label className={lbl}>Hours</label>
              <Input type="number" min="2" data-testid="edit-hours" value={form.hours} onChange={(e) => set("hours", e.target.value)} className={fld} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="edit-date" className={`${fld} w-full justify-start font-normal`}>
                    <CalendarIcon size={15} className="mr-2 text-primary" />
                    {date ? format(date, "MMM d, yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="bg-[#121212] border-white/10 p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className={lbl}>Time</label>
              <Input type="time" data-testid="edit-time" value={form.time} onChange={(e) => set("time", e.target.value)} className={fld} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Vehicle</label>
              <Select value={form.vehicle_type} onValueChange={(v) => { const cap = vehicles.find((x) => x.id === v)?.pax || 6; set("vehicle_type", v); if (Number(form.passengers) > cap) set("passengers", String(cap)); }}>
                <SelectTrigger data-testid="edit-vehicle" className={fld}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-foreground">
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={lbl}>Passengers</label>
              <Select value={form.passengers} onValueChange={(v) => set("passengers", v)}>
                <SelectTrigger data-testid="edit-passengers" className={fld}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-foreground">
                  {Array.from({ length: maxPax }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between border border-white/10 px-4 py-3">
            <span className="text-sm text-foreground/85">Meet &amp; Greet</span>
            <Switch checked={form.meet_greet} onCheckedChange={(v) => set("meet_greet", v)} data-testid="edit-meet-greet" />
          </div>

          <div>
            <label className={lbl}>Flight number (optional)</label>
            <Input data-testid="edit-flight" value={form.flight_number} onChange={(e) => set("flight_number", e.target.value)} className={fld} placeholder="e.g. UA123" />
          </div>
          <div>
            <label className={lbl}>Notes (optional)</label>
            <Input data-testid="edit-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className={fld} placeholder="Special requests" />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="edit-save"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wide w-full">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
